import { useMemo, useRef } from "react";
import type { RegisterStepSave, StepKeys, StepSaveState } from "../steps";

const WAIT_IDLE_INTERVAL_MS = 50;
const WAIT_IDLE_TIMEOUT_MS = 10000;

const sleep = (durationMs: number) =>
  new Promise(resolve => window.setTimeout(resolve, durationMs));

export const useStepSaveCoordinator = () => {
  const stepStateGettersRef = useRef<Map<StepKeys, () => StepSaveState>>(
    new Map()
  );

  const registerStepSave = useMemo<RegisterStepSave>(
    () => (stepKey, getState) => {
      stepStateGettersRef.current.set(stepKey, getState);

      return () => {
        if (stepStateGettersRef.current.get(stepKey) === getState) {
          stepStateGettersRef.current.delete(stepKey);
        }
      };
    },
    []
  );

  const getStepStates = () =>
    [...stepStateGettersRef.current.values()].map(getState => getState());

  const getInvalidSteps = () =>
    getStepStates().filter(state => state.isInvalid);
  const hasInvalidStep = () => getStepStates().some(state => state.isInvalid);

  const flushAll = async () => {
    if (hasInvalidStep()) {
      return;
    }

    const states = getStepStates();
    for (const state of states) {
      await state.flush();
    }
  };

  const validateAll = async () => {
    const states = getStepStates();
    for (const state of states) {
      await state.validateAllFields();
    }
  };

  const waitUntilIdle = async ({
    isMutating,
    timeoutMs = WAIT_IDLE_TIMEOUT_MS,
  }: {
    isMutating: () => boolean;
    timeoutMs?: number;
  }) => {
    const startedAt = Date.now();

    while (true) {
      const states = getStepStates();
      const hasPendingStepSaves = states.some(
        state => state.isSaving || state.isValidating
      );

      if (!hasPendingStepSaves && !isMutating()) {
        return;
      }

      if (Date.now() - startedAt > timeoutMs) {
        throw new Error("save_coordinator_timeout");
      }

      await sleep(WAIT_IDLE_INTERVAL_MS);
    }
  };

  return {
    registerStepSave,
    getInvalidSteps,
    hasInvalidStep,
    flushAll,
    waitUntilIdle,
    getStepStates,
    validateAll,
  };
};
